import { StaysPropertiesSelect } from '@/components/ui/inputs/StaysPropertiesSelect'
import {
  Box,
  Button,
  Fade,
  FormControl,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ScaleFade,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { Plus } from 'lucide-react'
import React, { useContext } from 'react'
import ErrorBoundary from '../../components/ErrorBoundary'
import PropertyFilters from '../../components/features/properties/PropertyFilters'
import PropertyList from '../../components/features/properties/PropertyList'
import {
  PropertiesContext,
  PropertiesContextType,
} from '../../contexts/PropertiesContext'

const PropertiesPageContent: React.FC = () => {
  const {
    handleNewProperty,
    selectedStaysProperty,
    syncStaysProperties,
    isSyncLoading,
  } = useContext<PropertiesContextType | undefined>(
    PropertiesContext
  ) as PropertiesContextType
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [showFirstBox, setShowFirstBox] = React.useState(true)

  return (
    <Box w="100%" minH="100vh" py={8} px={6}>
      <VStack spacing={{ base: 6, md: 8 }} align="stretch" maxW="none">
        {/* Page Header */}
        <HStack justify="space-between" align="flex-start">
          <Box>
            <Heading
              as="h1"
              size={{ base: 'lg', md: 'xl' }}
              color="gray.800"
              mb={2}
            >
              Acomodaçoes
            </Heading>
            <Box color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
              Gerencie todas as suas acomodaçoes cadastradas
            </Box>
          </Box>
          <Button
            leftIcon={<Icon as={Plus} />}
            colorScheme="blue"
            size={{ base: 'md', md: 'lg' }}
            onClick={onOpen}
            flexShrink={0}
          >
            Nova Acomodação
          </Button>
        </HStack>

        {/* Filters com Paginação Integrada */}
        <PropertyFilters />

        {/* Property List */}
        <ErrorBoundary fallbackMessage="Não foi possível carregar a lista de acomodações.">
          <PropertyList />
        </ErrorBoundary>
      </VStack>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          setShowFirstBox(true)
        }}
        size={'xl'}
        isCentered
        motionPreset="slideInBottom"
      >
        <ModalOverlay />
        <ModalContent padding={4} className="min-w-[300px] min-h-[300px]">
          {showFirstBox ? (
            <Fade in={showFirstBox}>
              <Box
                display="flex"
                flexDirection="column"
                gap={8}
                justifyContent={'space-between'}
              >
                <ModalHeader>Como deseja proceder?</ModalHeader>
                <ModalCloseButton />
                <ModalBody display="flex" flexDirection="column" gap={8}>
                  <Button
                    colorScheme="blue"
                    size={'lg'}
                    w={'full'}
                    onClick={async () => {
                      await syncStaysProperties()
                      setShowFirstBox((old) => !old)
                    }}
                    flexWrap={'wrap'}
                    wordBreak={'break-word'}
                    isLoading={isSyncLoading}
                    disabled={isSyncLoading}
                    loadingText="Sincronizando propriedades com a Stays"
                  >
                    <p>
                      <span>Criar nova acomodação</span>{' '}
                      <span className="font-bold">Vinculada a Stays</span>
                    </p>
                  </Button>
                  <Button
                    colorScheme="blue"
                    variant="outline"
                    size={'lg'}
                    w={'full'}
                    onClick={() => handleNewProperty()}
                    flexWrap={'wrap'}
                    wordBreak={'break-word'}
                  >
                    <p>
                      <span>Criar nova acomodação</span>{' '}
                      <span className="font-bold">NÃO Vinculada a Stays</span>
                    </p>
                  </Button>
                </ModalBody>
              </Box>
            </Fade>
          ) : (
            <ScaleFade initialScale={0.7} in={!showFirstBox}>
              <Box
                display="flex"
                flexDirection="column"
                gap={4}
                justifyContent={'space-between'}
              >
                <ModalHeader color={'blue.600'} fontWeight={'700'}>
                  Em qual propriedade deseja criar a acomodação?
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody display="flex" flexDirection="column" gap={8}>
                  <Box className="flex justify-end">
                    <Button
                      leftIcon={<Icon as={Plus} />}
                      type="button"
                      colorScheme="blue"
                      size={'md'}
                      variant="outline"
                      onClick={() => {
                        handleNewProperty(true)
                        setShowFirstBox(true)
                      }}
                    >
                      Propriedade Stays
                    </Button>
                  </Box>
                  <FormControl>
                    <StaysPropertiesSelect />
                  </FormControl>
                  <Button
                    colorScheme="blue"
                    size={'lg'}
                    w={'full'}
                    disabled={!selectedStaysProperty}
                    onClick={() => {
                      handleNewProperty()
                      setShowFirstBox(true)
                    }}
                  >
                    Avançar
                  </Button>
                </ModalBody>
              </Box>
            </ScaleFade>
          )}
        </ModalContent>
      </Modal>
    </Box>
  )
}

const PropertiesPage: React.FC = () => {
  return <PropertiesPageContent />
}

export default PropertiesPage
